import { IconMenu2 } from "@tabler/icons-react";
import { Link, useParams, useRouterState } from "@tanstack/react-router";
import { Breadcrumb, Button, Flex, Grid, Typography, theme } from "antd";
import { useState } from "react";
import { useWorkspaces } from "@/common/artemis-operations/workspaces";
import { PAGE_DOCS } from "@/common/page-docs";
import { ID_PATTERN, toLabel } from "@/common/utils";
import { usePageTitle } from "@/contexts/page-title-context";
import { CustomIcon, IdField } from "../../base";
import { ActionsBar } from "./actions-bar";
import { PageDocDrawer } from "./page-doc-drawer";

export function PageHeader({ onMenuClick }: { onMenuClick?: () => void }) {
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const routeId = useRouterState({ select: (s) => s.matches.at(-1)?.routeId });
	const {
		token: { colorBorder },
	} = theme.useToken();
	const { title } = usePageTitle();
	const [docOpen, setDocOpen] = useState(false);
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId?: string;
	};
	const { data: workspaces } = useWorkspaces();
	const workspaceName = workspaces?.find((w) => w?.id === workspaceId)?.name;

	const pageDoc = routeId ? PAGE_DOCS[routeId] : undefined;

	// The workspace id is an ID segment, but we want to show its name (not filter it out).
	const labelFor = (seg: string) =>
		seg === workspaceId ? toLabel(workspaceName ?? "Workspace") : toLabel(seg);

	const allSegments = pathname.split("/").filter(Boolean);
	const breadcrumbItems = allSegments
		.map((seg, i) => ({
			seg,
			path: `/${allSegments.slice(0, i + 1).join("/")}`,
			// The first segment is the cosmetic org slug — keep it in link paths, but don't render it as a crumb.
			isSlug: i === 0,
		}))
		.filter(({ isSlug }) => !isSlug)
		.map(({ seg, path }, i, arr) => ({
			title:
				i === arr.length - 1 ? (
					seg !== workspaceId && ID_PATTERN.test(seg) ? (
						<IdField id={seg} />
					) : (
						labelFor(seg)
					)
				) : (
					// ponytail: TanStack Router requires literal route types; cast is safe here
					<Link to={path as "/"}>{labelFor(seg)}</Link>
				),
		}));

	return (
		<div
			style={{
				borderBottom: `1px dashed ${colorBorder}`,
				paddingTop: 10,
				paddingBottom: 6,
				paddingLeft: isMobile ? 10 : 20,
				paddingRight: isMobile ? 10 : 24,
			}}
		>
			{!isMobile && breadcrumbItems.length > 0 && (
				<Breadcrumb items={breadcrumbItems} style={{ marginBottom: 2 }} />
			)}

			<Flex align="center" style={{ width: "100%" }}>
				{/* Left slot: on mobile it grows to match the right slot (centering the title)
				    and holds the hamburger; on desktop it collapses so the title sits left. */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-start",
						flex: isMobile ? 1 : "0 0 auto",
						minWidth: 0,
					}}
				>
					{isMobile && (
						<Button
							type="text"
							icon={<CustomIcon icon={IconMenu2} />}
							onClick={onMenuClick}
						/>
					)}
				</div>
				<Flex vertical align="start" justify="center" style={{ minWidth: 0 }}>
					<Typography.Title
						level={isMobile ? 5 : 4}
						ellipsis
						style={{
							maxWidth: isMobile ? 250 : 500,
							marginTop: isMobile ? 6 : 0,
							marginBottom: isMobile ? 2 : 0,
						}}
					>
						{toLabel(title)}
					</Typography.Title>
					{/* ponytail: no ellipsis — it would clip the "Learn more" link; keep descriptions to one short line */}
					{!isMobile && pageDoc?.description && (
						<Typography.Text
							ellipsis
							type="secondary"
							style={{ fontSize: 12, maxWidth: 500 }}
						>
							{pageDoc.description}
							{pageDoc.doc && (
								<>
									{" "}
									<Typography.Link
										style={{ fontSize: 12 }}
										onClick={() => setDocOpen(true)}
									>
										Learn more
									</Typography.Link>
								</>
							)}
						</Typography.Text>
					)}
				</Flex>
				{/* Right slot: actions pinned right; always grows so the title sits left
				    when actions exist and centered when they don't. */}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						flex: 1,
						minWidth: 0,
					}}
				>
					<ActionsBar />
				</div>
			</Flex>

			{pageDoc?.doc && docOpen && (
				<PageDocDrawer
					title={title}
					doc={pageDoc.doc}
					open={docOpen}
					onClose={() => setDocOpen(false)}
				/>
			)}
		</div>
	);
}
